// «👥 متابعات الفريق» ليومٍ واحد — كل بياع وما عمله، من غير فلاتر (قرار المالك 03/10/2026: «لما اضغط على متابعات الفريق يجبلى
// كل واحد عمل كل يوم متابعات ايه ونوعها ايه سواء بنك ارقام او جديد وكدا، بحيث ابقى عارف لو بصيت من غير ما اضغط على الفلاتر
// تحت كل واحد عمل ايه… وكذلك يبقى فيه كالندر اختار منه اليوم» — «واظهر الكلام دا عند محمد ابراهيم»).
//
// كارتٌ لكل بياع وشريك (لا جدول: لا شريط تمرير جانبي — قرار المالك 01/10/2026): المحسوب في التارجت أوّلًا، ثم المصادر
// الأربعة، ثم النتائج. ومن لم يسجّل شيئًا يظهر بصفر — الغياب عن الشغل يُرى. والضغط على الاسم يفلتر القوائم تحته.
import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, get } from '../lib/api.ts';
import { Alert, DateInput, Spinner } from '../components/ui.tsx';
import { addDays, cairoWorkDay, shortDate } from '../lib/format.ts';
import { SOURCES } from '../lib/labels.ts';

export interface TeamDayRow {
  salesUserId: string; salesName: string; role: string;
  contacts: number; counted: number; updates: number;
  /** عملاء جدد على الشركة: رقم لم يكن في البرنامج قبل المتابعة (المالك 04/10/2026) — نفس تعريف التارجت */
  newCustomers: number;
  leadBank: number; manual: number; webLead: number; siteChat: number;
  answered: number; noAnswer: number; unreachable: number;
  interested: number; readyToBook: number; notInterested: number; lost: number; convertedToBooking: number;
  normal: number; important: number; urgent: number;
}

/**
 * **كل رقمٍ يُضغط فينزل لعملائه** (قرار المالك 03/10/2026: «لما نضغط على حاجه فيهم سواء مهمه او تم الرد مثلا او مهتم ينزلوا على
 * قايمه العملاء دى ف اليوم وينقله الشاشه لتحت… كل نوع نضغط عليه يعمل كدا» — «ليا كأدمن ليهم كلهم… ومعاهم فى نطاق اعمالهم
 * هما فقط»): اليوم، والبياع (في الفريق)، والنوع المضغوط — والقوائم تحت تفتح عليها.
 */
export interface DayDrill {
  day: string;
  sellerId: string | null; sellerName: string | null;
  filter: { kind: 'source' | 'result' | 'status' | 'priority' | 'converted' | 'contact' | 'counted'; value: string; label: string } | null;
}

const SOURCE_KEY: Record<string, keyof TeamDayRow> = { LEAD_BANK: 'leadBank', MANUAL: 'manual', WEB_LEAD: 'webLead', SITE_CHAT: 'siteChat' };
const WEEKDAY = ['الأحد', 'الاتنين', 'التلات', 'الأربع', 'الخميس', 'الجمعة', 'السبت'];
const weekdayOf = (iso: string) => WEEKDAY[new Date(`${iso}T12:00:00Z`).getUTCDay()];

/**
 * `mine`: **يوم صاحبه وحده بنفس الكارت** (قرار المالك 03/10/2026: «خلى كل واحد من الشباب برضو يظهرله متابعاته كدا بنفس التقسيم
 * دا مش التقسيم الاول بس كل واحد يظهرله حاجته فقط») — بلا سطر الفريق، والخادم لا يُرجع غير صفّه.
 */
export function TeamDayPanel({ onDrill, mine = false }: { onDrill?: (d: DayDrill) => void; mine?: boolean }) {
  const today = cairoWorkDay();
  const [day, setDay] = useState(today);
  const [items, setItems] = useState<TeamDayRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setItems(null); setError(null);
    get<{ items: TeamDayRow[] }>(`/crm/team-day?on=${day}${mine ? '&mine=true' : ''}`)
      .then(r => { if (alive) setItems(r.items); })
      .catch(e => { if (alive) { setError(e instanceof ApiError ? e.message : 'تعذّر تحميل متابعات الفريق'); setItems([]); } });
    return () => { alive = false; };
  }, [day, mine]);

  const go = (r: TeamDayRow, filter: DayDrill['filter']) =>
    onDrill?.({ day, sellerId: mine ? null : r.salesUserId, sellerName: mine ? null : r.salesName, filter });
  const sum = (k: keyof TeamDayRow) => (items ?? []).reduce((t, r) => t + Number(r[k] ?? 0), 0);

  return (
    <section aria-label={mine ? 'متابعاتي' : 'متابعات الفريق'} className="space-y-3">
      {/* اليوم: سهمان والتقويم و«النهارده» */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setDay(d => addDays(d, -1))}>→ اليوم اللي قبله</button>
        <div className="w-44"><DateInput value={day} max={today} onChange={v => { if (v) setDay(v); }} /></div>
        <button type="button" className="btn-ghost px-3 py-1.5 text-sm" disabled={day >= today}
          onClick={() => setDay(d => (addDays(d, 1) > today ? today : addDays(d, 1)))}>اليوم اللي بعده ←</button>
        {day !== today && <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setDay(today)}>النهارده</button>}
        <span className="text-sm font-semibold text-ink-700">{weekdayOf(day)} {shortDate(day)}{day === today ? ' — النهارده' : ''}</span>
      </div>

      {error && <Alert>{error}</Alert>}
      {items === null ? <Spinner /> : (
        <>
          {/* إجمالي الفريق في سطر */}
          {!mine && <div className="flex flex-wrap gap-2 rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-900">
            <b>الفريق كله:</b>
            <span>اتحسبت في التارجت <b className="tabular-nums">{sum('counted')}</b></span>·
            <span>كل التواصلات <b className="tabular-nums">{sum('contacts')}</b></span>·
            {SOURCES.map(s => <span key={s.code}>{s.icon} {s.ar} <b className="tabular-nums">{sum(SOURCE_KEY[s.code])}</b></span>)}·
            <span>حجوزات <b className="tabular-nums">{sum('convertedToBooking')}</b></span>
          </div>}

          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map(r => {
              const idle = r.contacts === 0;
              return (
                <li key={r.salesUserId} className={`card p-3 ${idle ? 'opacity-70' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" className="text-start text-base font-bold text-brand-800 hover:underline"
                      aria-label={`متابعات ${r.salesName}`} onClick={() => go(r, null)}>
                      {r.salesName}
                    </button>
                    <button type="button" className="text-center leading-tight" aria-label={`اتحسبت في التارجت ${r.counted}`}
                      onClick={() => go(r, { kind: 'counted', value: 'true', label: 'اتحسبت في التارجت' })}>
                      <div className={`text-2xl font-bold tabular-nums ${idle ? 'text-ink-400' : 'text-emerald-700'}`}>{r.counted}</div>
                      <div className="text-[11px] text-ink-500">اتحسبت في التارجت</div>
                    </button>
                  </div>
                  {idle ? (
                    <p className="mt-2 text-sm text-ink-500">{mine ? 'ما سجّلتش متابعات في اليوم ده.' : 'ما سجّلش متابعات في اليوم ده.'}</p>
                  ) : (
                    <>
                      {/* **قائمةٌ منضبطة بعمودين** (المالك 05/10/2026 بعد الشرائط والمربّعات: «رتب الشكل دا برضو مش مرتب»): كل بند
                          سطرٌ فيه الاسم على اليمين والرقم على اليسار في عمودٍ واحد مرصوص، بندان في كل صفّ، بلا أيقوناتٍ ولا
                          أرضيات — فتُقرأ الأرقام كشفًا لا نثرًا. والعناوين فواصل باهتة. وكلُّ بندٍ يُضغط فينزل أصحابه، والصفر باهت.
                          والقسم الأوّل: التواصلات = جديد على الشركة + تحديث موقف + أرقام معروفة للشركة كلّمها أوّل مرة (04/10/2026). */}
                      <dl className="mt-2 text-xs">
                        <Row label="التواصلات">
                          <Item label="كل التواصلات" n={r.contacts} onClick={() => go(r, null)} />
                          <Item label="جديد على الشركة" n={Number(r.newCustomers)} tone="text-emerald-700"
                            onClick={() => go(r, { kind: 'contact', value: 'new', label: 'جديد على الشركة' })} />
                          <Item label="تحديث موقف" n={Number(r.updates)} tone="text-sky-700"
                            onClick={() => go(r, { kind: 'contact', value: 'update', label: 'تحديث موقف' })} />
                        </Row>
                        <Row label="المصدر">
                          {SOURCES.map(src => (
                            <Item key={src.code} label={src.ar} n={Number(r[SOURCE_KEY[src.code]])}
                              onClick={() => go(r, { kind: 'source', value: src.code, label: src.ar })} />
                          ))}
                        </Row>
                        <Row label="الأولوية">
                          {PRIORITY_CHIPS.map(p => (
                            <Item key={p.code} label={p.ar} n={Number(r[p.key])} tone={p.numTone}
                              onClick={() => go(r, { kind: 'priority', value: p.code, label: p.ar })} />
                          ))}
                        </Row>
                        <Row label="النتيجة">
                          {MINIS.map(m => (
                            <Item key={m.label} label={m.label} n={Number(r[m.key])} tone={m.tone} onClick={() => go(r, m.filter)} />
                          ))}
                        </Row>
                      </dl>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

// الأولوية (المالك 03/10/2026: «عاوزك تزود الاولوية مهمه او عاجله»)
const PRIORITY_CHIPS: { code: string; ar: string; icon: string; key: keyof TeamDayRow; numTone?: string }[] = [
  { code: 'NORMAL', ar: 'عادية', icon: '⚪', key: 'normal' },
  { code: 'IMPORTANT', ar: 'مهمة', icon: '🟠', key: 'important', numTone: 'text-amber-700' },
  { code: 'URGENT', ar: 'عاجلة', icon: '🔴', key: 'urgent', numTone: 'text-rose-700' },
];
const MINIS: { label: string; key: keyof TeamDayRow; tone?: string; filter: DayDrill['filter'] }[] = [
  { label: 'تم الرد', key: 'answered', filter: { kind: 'result', value: 'ANSWERED', label: 'تم الرد' } },
  { label: 'لم يرد', key: 'noAnswer', filter: { kind: 'result', value: 'NO_ANSWER', label: 'لم يرد' } },
  { label: 'مغلق', key: 'unreachable', filter: { kind: 'result', value: 'UNREACHABLE', label: 'الرقم مغلق أو غير متاح' } },
  { label: 'مهتم', key: 'interested', filter: { kind: 'status', value: 'INTERESTED', label: 'مهتم' } },
  { label: 'جاهز للحجز', key: 'readyToBook', filter: { kind: 'status', value: 'READY_TO_BOOK', label: 'جاهز للحجز' } },
  { label: 'حجوزات', key: 'convertedToBooking', tone: 'text-emerald-700', filter: { kind: 'converted', value: 'true', label: 'اتحوّلت لحجز' } },
  { label: 'غير مهتم', key: 'notInterested', tone: 'text-rose-700', filter: { kind: 'status', value: 'NOT_INTERESTED', label: 'غير مهتم' } },
  { label: 'فقدناه', key: 'lost', tone: 'text-rose-700', filter: { kind: 'status', value: 'LOST', label: 'فقدنا العميل' } },
];


/** قسمٌ: عنوانٌ باهت بخطٍّ رفيع فوقه، ثم بنوده بندان في الصفّ */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-1.5 first:mt-0">
      <dt className="border-t border-slate-100 pt-1 text-[10px] font-semibold text-ink-400 first:border-0">{label}</dt>
      <dd className="grid grid-cols-2 gap-x-4">{children}</dd>
    </div>
  );
}

/** بندٌ: الاسم يمينًا والرقم يسارًا في عمودٍ مرصوص — يُضغط فينزل أصحابه؛ والصفر باهت ولا يُضغط */
function Item({ label, n: raw, tone, onClick }: { label: string; n: number; tone?: string; onClick: () => void }) {
  const n = Number.isFinite(raw) ? raw : 0;       // صفٌّ قديم بلا الحقل الجديد ⇒ صفر لا NaN
  return (
    <button type="button" disabled={!n} onClick={onClick} aria-label={`${label} ${n}`}
      className={`flex items-baseline justify-between gap-2 rounded px-1 py-0.5 text-start leading-5 ${n ? 'text-ink-700 hover:bg-brand-50 hover:text-brand-800' : 'text-ink-400'}`}>
      <span className="truncate">{label}</span>
      <b className={`shrink-0 tabular-nums ${n ? (tone ?? 'text-ink-900') : 'font-normal'}`}>{n}</b>
    </button>
  );
}
